<script setup>
import { ref, computed } from 'vue'
import Staff from '@pages/Shared/layouts/StaffLayout.vue'
import Layout from '@pages/Shared/layouts/AdminLayout.vue';
import Modal from '@pages/Shared/Modal.vue';
import Input from '@pages/Shared/form/Input.vue'; 
import Select from '@pages/Shared/form/Select.vue';
import MultiSelect from '@pages/Shared/form/MultiSelect.vue';
import Textarea from '@pages/Shared/form/Textarea.vue';
import Prompt from '@pages/Shared/Prompt.vue';
import Pagination from '@pages/Shared/Pagination.vue';
import userMale from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'
// Sample class and students
const selectedClass = ref('')
const classes = ref(['SS1A', 'SS1B', 'SS2A', 'SS2B'])

const students = ref([
  { id: 1, name: 'Aisha Musa', admission: 'ST001', present: false },
  { id: 2, name: 'Yusuf Bello', admission: 'ST002', present: true },
  { id: 3, name: 'Fatima Ahmed', admission: 'ST003', present: false },
  { id: 4, name: 'Ibrahim Ali', admission: 'ST004', present: true },
])

const date = ref(new Date().toISOString().substr(0, 10))
const search = ref('')

// Filter students by name or admission
const filteredStudents = computed(() => {
  if (!search.value) return students.value
  return students.value.filter(
    s =>
      s.name.toLowerCase().includes(search.value.toLowerCase()) ||
      s.admission.toLowerCase().includes(search.value.toLowerCase())
  )
})

const presentCount = computed(() => students.value.filter(s => s.present).length)
const absentCount = computed(() => students.value.filter(s => !s.present).length)

const toggleAttendance = (student) => {
  student.present = !student.present
}

const submitAttendance = () => {
  const result = {
    class: selectedClass.value,
    date: date.value,
    attendance: students.value.map(s => ({
      id: s.id,
      name: s.name,
      admission: s.admission,
      status: s.present ? 'Present' : 'Absent'
    }))
  }

  console.log('Attendance submitted:', result)
  alert('Attendance recorded successfully!')
}
</script>

<template>
  <Staff>
    <div class="w-[90vw] md:max-w-[85vw] mx-auto py-6 space-y-8">
      <!-- Header -->
      <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 class="text-2xl font-bold text-gray-700 mb-4 sm:mb-0">Class Attendance</h1>
        <div class="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select
            v-model="selectedClass"
            class="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select Class</option>
            <option v-for="cls in classes" :key="cls" :value="cls">{{ cls }}</option>
          </select>

          <input
            type="date"
            v-model="date"
            class="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />

          <input
            type="text"
            v-model="search"
            placeholder="Search student..."
            class="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <!-- Summary -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div class="bg-primary-50 p-4 rounded-xl shadow">
          <h2 class="text-gray-500 text-sm">Total Students</h2>
          <p class="text-2xl font-bold text-primary-600">{{ students.length }}</p>
        </div>
        <div class="bg-green-50 p-4 rounded-xl shadow">
          <h2 class="text-gray-500 text-sm">Present</h2>
          <p class="text-2xl font-bold text-green-600">{{ presentCount }}</p>
        </div>
        <div class="bg-red-50 p-4 rounded-xl shadow">
          <h2 class="text-gray-500 text-sm">Absent</h2>
          <p class="text-2xl font-bold text-red-600">{{ absentCount }}</p>
        </div>
      </div>

      <!-- Table -->
      <div class="bg-white shadow rounded-xl overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Admission No</th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="(student, index) in filteredStudents"
              :key="student.id"
              class="hover:bg-gray-50"
            >
              <td class="px-6 py-3 text-gray-600">{{ index + 1 }}</td>
              <td class="px-6 py-3 text-gray-700 font-medium">{{ student.name }}</td>
              <td class="px-6 py-3 text-gray-600">{{ student.admission }}</td>
              <td class="px-6 py-3">
                <button
                  @click="toggleAttendance(student)"
                  :class="[
                    'px-4 py-1 rounded-full text-sm font-semibold transition',
                    student.present
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  ]"
                >
                  {{ student.present ? 'Present' : 'Absent' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Submit Button -->
      <div class="mt-6 text-right">
        <button
          @click="submitAttendance"
          class="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700"
        >
          Submit Attendance
        </button>
      </div>
    </div>
  </Staff>
</template>
